import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";

import { AdminError, ERRS, type Row } from "./api";
import { useAdmin } from "./context";
import { caseFor, COMFORT, comfortShort, day, icUrl, L, ml, pickAct, weekA, when } from "./format";

// Change programme, v3.1 PA4 → PA4R → PA4F (DEC-1164): edit → before and after → save. The server re-checks every
// rule; the checks here only stop an obviously incomplete form, with the same plain words the server would answer.
type Form = {
  act: string; reason: string; case_type: string; case_id: string; link: string; ref: string;
  /** null = untouched: the field shows the value in force (state loads after the panel opens). */
  step: string | null; dose: string | null; rung: string | null; wstep: string | null; wdef: boolean;
  goal_level_2: string; goal_top: string; product_key: string; instructions: string | null;
};

/** The six goals, in the options' order (each sub-goal row carries its top-level goal). */
export const topGoals = (goals: Row[]) => [...new Set(goals.map((g) => g.goal).filter(Boolean))] as string[];
/** The sub-goals of one top-level goal; `keep` (hers, under her own goal) stays listed even if the options drop it. */
export function subGoals(goals: Row[], top: string, keep: string): Row[] {
  const out = goals.filter((g) => g.goal === top);
  return keep && !out.some((g) => g.goal_level_2 === keep) ? [{ goal: top, goal_level_2: keep }, ...out] : out;
}
/** The four products: one entry per name, the customer's own key kept for her product (flavours are one product here). */
export function productChoices(products: Row[], nowKey: string, nowName: string | undefined): [string, string][] {
  const out = new Map<string, string>();
  for (const x of products) {
    const name = String(x.name || x.product_key);
    if (!out.has(name) || x.product_key === nowKey) out.set(name, x.product_key);
  }
  if (nowKey && ![...out.values()].includes(nowKey)) out.set(nowName || nowKey, nowKey);
  return [...out].map(([name, k]) => [k, name]);
}

/** A plain native select: the form needs empty-string options ("Keep", "Pick one"), which Radix Select refuses. */
export function Pick({ id, value, onChange, options, disabled, invalid }: {
  id: string; value: string; onChange: (v: string) => void; options: [string, string][]; disabled?: boolean; invalid?: boolean;
}) {
  return (
    <select id={id} value={value} disabled={disabled} aria-invalid={invalid || undefined} onChange={(e) => onChange(e.target.value)}
      className="h-9 w-full rounded-lg border border-input bg-transparent px-2 text-sm aria-invalid:border-destructive">
      {options.map(([k, t]) => <option key={k} value={k}>{t}</option>)}
    </select>
  );
}

export const Help = ({ children, id }: { children: React.ReactNode; id?: string }) => (
  <p id={id} className="mt-1 text-xs text-ks-grey-1">{children}</p>
);
export const FieldError = ({ children, id }: { children: React.ReactNode; id: string }) => (
  <p id={id} role="alert" className="mt-1 rounded px-2 py-1 text-xs">{children}</p>
);

/** PA4F words (DEC-1164), with her live target. */
export const aboveTarget = (max: unknown) => `Above her ${max} ml target. Enter ${max} ml or less.`;
export const SAVE_FAILED = "Couldn’t save. Nothing changed. Your edits are kept below.";

/**
 * A refusal about the customer's state, not the form (409): saving again can never work, so the words say what
 * to do instead (Damien 29 Oct: every error carries a one-click fix). Null for any other key.
 */
export function refusalNextStep(key: string | null, first: string): string | null {
  const who = first || "This customer";
  if (key === "no_active_run")
    return `${who} hasn’t done her first check-in yet, so her programme can’t be changed here. Next step: message her in Intercom and ask her to open the app and do her first check-in. Once she has, you can change her programme here.`;
  if (key === "no_start_answers")
    return `${who} hasn’t answered the start questions, so a new goal can’t be built for her. Next step: message her in Intercom and ask her to finish the start questions in the app. Then change her goal here.`;
  return null;
}

/**
 * The refusal's one-click Open in Intercom: her open alert or hand-off ticket, else the newest item from
 * `customers/<id>/intercom` (a no_active_run customer has no check-in, so normally no alert ticket). Null when
 * neither exists.
 */
export function refusalLink(ticket: string | null | undefined, ic: Row | undefined): string | null {
  if (ticket) return icUrl(String(ticket));
  const top = ((ic?.items || []) as Row[]).find((x) => x.id);
  return top ? icUrl(String(top.id)) : null;
}

/** No Intercom link to give: say why, and the next step, in plain words. */
export function noIntercomLink(note: unknown, email: unknown): string {
  const em = email ? `her email, ${email}` : "her email";
  if (note === "intercom_off") return `Intercom isn’t connected here, so there’s no link to her conversation. Next step: open Intercom and search for ${em}.`;
  if (note === "intercom_error") return `Intercom didn’t answer, so there’s no link to her conversation. Next step: open Intercom and search for ${em}.`;
  if (note === "no_contact") return `She isn’t in Intercom yet. Next step: start a new conversation in Intercom to ${em}.`;
  return `She has no Intercom tickets or conversations yet. Next step: start a new conversation in Intercom to ${em}.`;
}
/** A new goal builds its plan from her start answers (goal_change_alone), so Review never shows today's numbers. */
export const GOAL_SETS = "Set by the new goal from her start answers";

/** "1 · 5 ml" … "4 · 12.5 ml (now)" … "5 · 15 ml (target)". */
export function stepOptions(steps: Row[], now: number | null | undefined): [string, string][] {
  return steps.map((x) => [
    String(x.step),
    `${x.step} · ${x.dose_ml} ml${x.step === now ? " (now)" : x.target ? " (target)" : ""}`,
  ]);
}
/** The dose a picked step sets (the API maps it again and refuses a mismatch). */
export const stepDose = (steps: Row[], step: string | null | undefined) =>
  step ? steps.find((x) => String(x.step) === step)?.dose_ml ?? null : null;
/** The step a dose sits on exactly, or null when the dose is between steps. */
export const doseStep = (steps: Row[], dose: number | null) =>
  dose == null ? null : steps.find((x) => Number(x.dose_ml) === dose)?.step ?? null;

/** "10 → 11.5 → 13 → 14.5 → 15 ml. Reaches target on 2 Dec." from the API's dry run. */
export function journeyLine(pv: Row | undefined): string {
  if (!pv) return "";
  const doses = [pv.dose_ml, ...((pv.planned_ahead || []) as Row[]).map((x) => x.dose_ml)].filter((x) => x != null);
  const uniq = doses.filter((x, i) => i === 0 || Number(x) !== Number(doses[i - 1]));
  if (!uniq.length) return "";
  return `${uniq.join(" → ")} ml.${pv.reaches_target_date ? ` Reaches target on ${day(pv.reaches_target_date)}.` : " Holds here."}`;
}

type Changes = {
  dose: number | null; step: number | null; rung: number | null; wstep: number | null; wdef: boolean;
  goal: string | null; prod: string | null; instructions: string | null;
};
/**
 * What Save sends. A dose change (set-dose, or resume when paused) always carries the stomach comfort (rung) in
 * force or picked, so a step change never drops it; the picked plan step goes with its dose and the API maps it again.
 * A product change never sends a step: only the database knows the new product's steps (step_with_product_change).
 */
export function saveBody({ act, route, f, v, dose, step, rung }: {
  act: string; route: string; f: Pick<Form, "reason" | "case_type" | "case_id" | "link" | "ref">; v: Changes;
  dose: number | null; step: number | null; rung: number;
}): Row {
  const body: Row = { reason: f.reason.trim(), case_type: f.case_type };
  if (f.case_id.trim()) body.case_id = f.case_id.trim();
  if (f.link) {
    const [k, lid] = f.link.split(":");
    body[k === "alert" ? "team_alert_id" : "handoff_id"] = lid;
  }
  if (act === "set-dose" || act === "resume") {
    if (route !== "change-program" || v.dose != null || v.step != null) body.dose_ml = dose;
    if (step != null && !v.prod && (v.step != null || route !== "change-program")) body.step = step;
    if (route !== "change-program" || v.rung != null) body.rung = rung;
  }
  if (route === "change-program") {
    if (v.goal) body.goal_level_2 = v.goal;
    if (v.prod) body.product_key = v.prod;
    if (v.instructions != null) body.instructions = v.instructions;
    if (v.wdef) body.weekly_step_default = true;
    else if (v.wstep != null) body.weekly_step_ml = v.wstep;
  }
  if (act === "apply-version") body.ref_id = f.ref;
  return body;
}

/**
 * The dry-run query for Review's new journey, or null when nothing the journey depends on changes. "Back to the
 * protocol default" moves the weekly increase too, so it replots (the API reads `weekly_step_ml=default`). With a
 * new weekly increase the step list changes, so the dose the form shows is sent, never the step number.
 */
export function previewQuery(v: Pick<Changes, "step" | "dose" | "wstep" | "wdef">, dose: unknown): string | null {
  if (v.step == null && v.dose == null && v.wstep == null && !v.wdef) return null;
  const q = new URLSearchParams();
  if (v.step != null && v.wstep == null && !v.wdef) q.set("step", String(v.step));
  else q.set("dose_ml", String(dose));
  if (v.wdef) q.set("weekly_step_ml", "default");
  else if (v.wstep != null) q.set("weekly_step_ml", String(v.wstep));
  return q.toString();
}

/** Review words for a picked item that has left the open list (only for the one render before it is cleared). */
export const LINK_GONE = "No longer open: someone else handled it.";
/** Shown once a picked item left the open list (a colleague handled it) and Answers was cleared for it. */
export const LINK_HANDLED = "The item this change answered was handled by someone else, so Answers is now “Nothing open”. Save makes a new case ID.";
/** Shown after the unknown_case_id fix button unlinks the item. */
export const unlinked = (label: string) => `Answers is now “Nothing open”: this change no longer answers ${label}. Save makes a new case ID.`;

/** The Review line for the open item a change answers: "Nothing open" when none is picked. */
export const linkLabel = (links: [string, string][], link: string) =>
  !link ? "Nothing open" : links.find(([k]) => k === link)?.[1] ?? LINK_GONE;

export function ChangeProgramme({
  id, d, sv, openA, openH, open, onOpenChange, onSaved,
}: {
  id: string; d: Row; sv: Row[] | null; openA: Row[]; openH: Row[];
  open: boolean; onOpenChange: (o: boolean) => void; onSaved: (msg: string) => void;
}) {
  const { key, api, me, caseTypes, product, options } = useAdmin();
  const [stage, setStage] = useState<"edit" | "review">("edit");
  const [error, setError] = useState<string | null>(null);
  const [fieldErr, setFieldErr] = useState<{ field: "dose" | "step"; text: string } | null>(null);
  const [failed, setFailed] = useState(false);
  /** A customer-state refusal (409) with its next step: replaces "Try Save again". */
  const [blocked, setBlocked] = useState<string | null>(null);
  /** The server no longer knows the case ID (unknown_case_id): its one-click fix is Answers → "Nothing open". */
  const [caseGone, setCaseGone] = useState(false);
  /** Why Answers changed without staff picking it (LINK_HANDLED or unlinked()). */
  const [linkNote, setLinkNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /** The action Review opened with: frozen, so a late state load cannot change what Save sends (os-portal PR 712). */
  const [frozen, setFrozen] = useState<string | null>(null);
  const enc = encodeURIComponent(id);
  const state = useQuery({ queryKey: [key, "customer", id, "state"], queryFn: () => api.get<Row>(`customers/${enc}/state`), enabled: open });
  const s: Row = state.data || {};
  const plan: Row = d.plan || {};
  const pr: Row = d.profile || {};
  const first = String(pr.name || "").trim().split(/\s+/)[0] || "She";
  // The customer's open Intercom ticket, for the refusal's one-click next step.
  const ticket = [...openH, ...openA].map((x) => x.ticket).find(Boolean) as string | undefined;
  // Her tickets and conversations, only when a refusal needs a link and no open alert or hand-off has one.
  const ic = useQuery({
    queryKey: [key, "customer", id, "intercom"],
    queryFn: () => api.get<Row>(`customers/${enc}/intercom`).catch(() => ({ items: [], note: "intercom_error" })),
    enabled: !!blocked && !ticket,
  });
  const icHref = refusalLink(ticket, ic.data);
  const chg = !!me?.change_program;
  const paused = !!(s.paused ?? plan.status === "paused");
  const target = plan.target_dose_ml ?? s.target_dose_ml;
  const nowStep: number | null = plan.step_index ?? null;
  const goalNow: string = plan.goal_level_2 || "";

  const acts: [string, string][] = paused
    ? [["resume", "Change dose or step"]]
    : [["set-dose", "Change dose or step"], ["pause", "Pause the plan"]];
  // D14: a previous version of this goal's run, applied again as a new version (admin_apply_version).
  const olds = (sv || []).filter((x) => x.current_run).slice(1);
  if (chg && olds.length) acts.push(["apply-version", "Start from a previous plan"]);
  const oldTxt = (x: Row) =>
    `v${x.version_no} · ${day(x.at)} · ${x.paused ? "paused" : `${x.step != null ? `step ${x.step} · ` : ""}${x.dose_ml} ml · stomach comfort ${x.rung ?? "—"} · ${product(x.product_key)}`}${x.source === "staff" ? ` · ${x.staff_email}` : " · algorithm"}`;
  const links: [string, string][] = [
    ...openA.map((a): [string, string] => [`alert:${a.id}`, `Alert: ${L(a.type)}, ${when(a.at)}`]),
    ...openH.map((h): [string, string] => [`handoff:${h.id}`, `Hand-off: ${h.goal || ""}, ${when(h.at)}`]),
  ];

  const blank = (): Form => ({
    // Answers the open item at the top of her page by default (Damien 31 Oct: the case comes from it).
    act: acts[0]?.[0] ?? "set-dose", reason: "", case_type: "", case_id: "", link: links[0]?.[0] ?? "", ref: "",
    step: null, dose: null, rung: null, wstep: null, wdef: false, goal_level_2: "", goal_top: "", product_key: "", instructions: null,
  });
  const [f, setF] = useState<Form>(blank);
  const set = (patch: Partial<Form>) => setF((x) => ({ ...x, ...patch }));
  // Never typed (Damien 31 Oct): the case of the item answered, else a new ID the database makes on save.
  const caseId = caseFor(f.link, d.overrides as Row[] | undefined);
  /** True from the reset on open until the commit after it: that commit still holds the last session's form. */
  const opening = useRef(false);
  useEffect(() => {
    if (open) { setStage("edit"); setError(null); setFieldErr(null); setFailed(false); setBlocked(null); setCaseGone(false); setLinkNote(null); setFrozen(null); setF(blank()); opening.current = true; }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  // A colleague can handle the picked alert or hand-off while the panel is open (a focus refetch drops it from the
  // open list): clear the pick once and say so, so Save never sends a handled item's ID and the picker never shows
  // "Nothing open" over a stale pick. Skipped on the opening commit, which still sees the last session's form.
  const linkKeys = links.map(([k]) => k).join("|");
  useEffect(() => {
    if (!open) return;
    if (opening.current) { opening.current = false; return; }
    if (f.link && !links.some(([k]) => k === f.link)) { setLinkNote(LINK_HANDLED); set({ link: "" }); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, linkKeys, f.link]);
  // The state loads after the panel opens, so the offered actions can change under the form: the action the form
  // shows is always one still offered; once Review opens it is frozen.
  const act = frozen ?? pickAct(acts, f.act);
  const dosing = act === "set-dose" || act === "resume";

  // The fields in force, then what the form changes.
  const curDose = s.dose_ml ?? plan.dose_ml;
  const curRung = s.rung ?? plan.rung;
  const curW = s.weekly_step_ml ?? plan.weekly_step_ml;
  const stepText = f.step ?? (nowStep != null ? String(nowStep) : "");
  const doseText = f.dose ?? (curDose != null ? String(curDose) : "");
  const rungText = f.rung ?? (curRung != null ? String(curRung) : "0");
  const wText = f.wstep ?? (curW != null ? String(curW) : "");
  const wNew = dosing && !f.wdef && f.wstep != null && f.wstep.trim() !== "" && Number(f.wstep) !== Number(curW) ? Number(f.wstep) : null;
  // The API maps a step on the proposed plan: once the weekly increase changes, the step list is that plan's.
  const ladderQ = previewQuery({ step: null, dose: null, wstep: wNew, wdef: dosing && f.wdef }, curDose);
  const ladder = useQuery({
    queryKey: [key, "customer", id, "preview", ladderQ],
    queryFn: () => api.get<Row>(`customers/${enc}/preview?${ladderQ}`),
    enabled: open && ladderQ != null,
    retry: false,
  });
  const steps: Row[] = ((ladderQ != null ? ladder.data?.steps : plan.plan_steps) || []) as Row[];
  const stepsWait = ladderQ != null && !ladder.data && !ladder.error;
  const instrText = f.instructions ?? s.instructions ?? plan.instructions ?? "";
  const dose = doseText.trim() === "" ? null : Number(doseText);
  const pickedStep = stepText && stepDose(steps, stepText) != null && Number(stepDose(steps, stepText)) === dose ? Number(stepText) : null;
  const v: Changes = {
    dose: dosing && dose != null && dose !== Number(curDose) ? dose : null,
    step: dosing && !f.product_key && pickedStep != null && pickedStep !== nowStep ? pickedStep : null,
    rung: dosing && rungText !== String(curRung ?? "") ? Number(rungText) : null,
    wstep: wNew,
    wdef: dosing && f.wdef,
    goal: dosing && f.goal_level_2 && f.goal_level_2 !== goalNow ? f.goal_level_2 : null,
    prod: dosing && f.product_key ? f.product_key : null,
    instructions: dosing && f.instructions != null && f.instructions.trim() !== (s.instructions || plan.instructions || "") ? f.instructions.trim() : null,
  };
  // Weekly increase, goal, product and instructions save through change-program; dose, step and comfort alone keep
  // their own route (set-dose, or resume when paused).
  const programme = v.wstep != null || v.wdef || !!v.goal || !!v.prod || v.instructions != null;
  const route = act === "set-dose" && programme ? "change-program" : act;

  const pq = dosing ? previewQuery(v, dose) : null;
  const pv = useQuery({
    queryKey: [key, "customer", id, "preview", pq],
    queryFn: () => api.get<Row>(`customers/${enc}/preview?${pq}`),
    enabled: open && pq != null,
    retry: false,
  });

  const check = (): string | null => {
    if (!f.reason.trim()) return "reason_required";
    if (!f.case_type) return "unknown_case_type";
    if (act === "apply-version" && !f.ref) return "unknown_version";
    if (dosing && dose == null) return "bad_dose";
    if (programme && !chg) return "change_program_off";
    if (v.wstep != null && !(v.wstep >= 0)) return "bad_weekly_step";
    if (v.goal && (v.dose != null || v.step != null || v.rung != null || v.wstep != null || v.wdef)) return "goal_change_alone";
    if (f.goal_top && !f.goal_level_2) return "bad_goal";
    if (act === "set-dose" && v.dose == null && v.step == null && v.rung == null && !programme) return "nothing_changed";
    return null;
  };
  const doseOver = dosing && dose != null && target != null && dose > Number(target);

  const save = async () => {
    setBusy(true);
    setError(null);
    setFieldErr(null);
    const body = saveBody({ act, route, f: { ...f, case_id: caseId ?? "" }, v, dose, step: pickedStep, rung: Number(rungText) });
    try {
      await api.post<Row>(`customers/${enc}/${route}`, body);
      onOpenChange(false);
      onSaved(`Saved. ${first} sees this next time she opens the app.`);
    } catch (e) {
      const k = e instanceof AdminError ? e.key : null;
      // PA4F: back to the form with every edit kept; a refused field says so on the field.
      setStage("edit");
      setFrozen(null);
      const next = e instanceof AdminError && e.status === 409 ? refusalNextStep(k, String(pr.name || "").trim().split(/\s+/)[0]) : null;
      setBlocked(next);
      // unknown_case_id: saving again resends the same case ID, so no "Try Save again"; its own fix replaces it.
      setCaseGone(k === "unknown_case_id");
      setFailed(!next && k !== "unknown_case_id");
      if (next || k === "unknown_case_id") return;
      if (k === "above_target") setFieldErr({ field: "dose", text: aboveTarget((e as AdminError).body.max_ml ?? target) });
      else if (k === "step_dose_mismatch") setFieldErr({ field: "dose", text: ERRS.step_dose_mismatch });
      else if (k === "bad_step") setFieldErr({ field: "step", text: ERRS.bad_step });
      else if (k === "step_with_product_change") setFieldErr({ field: "step", text: ERRS.step_with_product_change });
      else setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const fmtStep = (n: number | null, ml_: unknown) => (n == null ? ml(ml_) : `${n} · ${ml_ ?? "—"} ml`);
  const afterStep = v.prod ? null : v.step ?? doseStep(steps, dose);
  const summary = `Now: step ${nowStep ?? "—"} · ${ml(curDose)}${paused ? " · paused" : ""} · stomach comfort ${curRung ?? "—"} · ${weekA(curW)} · target ${ml(target)}${steps.length ? ` (step ${steps.length})` : ""}`;
  const old = act === "apply-version" ? olds.find((x) => x.ref_id === f.ref) : null;
  const reachesAfter = pq != null ? pv.data?.reaches_target_date : plan.reaches_target_date;
  const reviewRows: [string, string, string][] = act === "pause"
    ? [["Plan", `${ml(curDose)} · on plan`, `Paused, ${ml(curDose)} held`]]
    : old
      ? [["Plan", `${fmtStep(nowStep, curDose)} · stomach comfort ${curRung ?? "—"}`, oldTxt(old)]]
      : ([
          ["Dose", ml(curDose), v.goal ? GOAL_SETS : ml(dose)],
          ["Weekly increase", weekA(curW), v.wdef ? `Back to the protocol default${s.weekly_step_default_ml != null ? `, ${weekA(s.weekly_step_default_ml)}` : ""}` : weekA(v.wstep ?? curW)],
          ["Step", fmtStep(nowStep, curDose), v.goal ? GOAL_SETS : fmtStep(afterStep, dose)],
          ["Stomach comfort", comfortShort(curRung), comfortShort(rungText)],
          [`Reaches ${ml(target)}`, plan.reaches_target_date ? day(plan.reaches_target_date) : "—", v.goal ? GOAL_SETS : reachesAfter ? day(reachesAfter) : pv.isLoading ? "…" : "—"],
          ...(v.goal ? [["Goal", plan.goal_level_2 || "—", v.goal] as [string, string, string]] : []),
          ...(v.prod ? [["Product", plan.product || "—", product(v.prod)] as [string, string, string]] : []),
          ...(v.instructions != null ? [["How to take it", s.instructions || plan.instructions || "—", v.instructions || "Removed"] as [string, string, string]] : []),
        ] as [string, string, string][]);

  const goals = ((options?.goals || []) as Row[]);
  const products = ((options?.products || []) as Row[]);
  const tops = topGoals(goals);
  const topNow = plan.goal || goals.find((g) => g.goal_level_2 === goalNow)?.goal || "";
  const prodNow = plan.product_key || s.product_key || "";
  /** The sub-goals under the goal picked (or hers): she can move within her own goal too. */
  const subs = subGoals(goals, f.goal_top || topNow, f.goal_top ? "" : goalNow);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetTrigger asChild><Button>Change programme</Button></SheetTrigger>
      <SheetContent className="w-[480px] overflow-auto sm:max-w-[480px]">
        <SheetHeader>
          <SheetTitle>Change programme: {first}</SheetTitle>
          <SheetDescription>{summary}</SheetDescription>
        </SheetHeader>
        {state.error ? (
          <Alert className="mx-4"><AlertDescription>{(state.error as Error).message}</AlertDescription></Alert>
        ) : stage === "edit" ? (
          <div className="space-y-3 px-4 pb-4 text-sm">
            {blocked ? (
              <Alert role="alert">
                <AlertDescription className="space-y-2">
                  <p>{blocked}</p>
                  {icHref ? (
                    <Button size="sm" variant="secondary" asChild>
                      <a href={icHref} target="_blank" rel="noopener noreferrer" data-nonmutating>Open in Intercom</a>
                    </Button>
                  ) : ic.isLoading ? (
                    <p className="text-ks-grey-1">Finding her Intercom conversation…</p>
                  ) : (
                    <p>{noIntercomLink(ic.data?.note, pr.email)}</p>
                  )}
                </AlertDescription>
              </Alert>
            ) : null}
            {caseGone ? (
              <Alert role="alert">
                <AlertDescription className="flex flex-wrap items-center justify-between gap-2">
                  <span>{ERRS.unknown_case_id}</span>
                  <Button size="sm" variant="secondary" data-nonmutating onClick={() => { setLinkNote(f.link ? unlinked(linkLabel(links, f.link)) : null); set({ link: "" }); setCaseGone(false); }}>Set Answers to Nothing open</Button>
                </AlertDescription>
              </Alert>
            ) : null}
            {failed ? (
              <Alert>
                <AlertDescription className="flex flex-wrap items-center justify-between gap-2">
                  <span>{SAVE_FAILED}</span>
                  <Button size="sm" variant="secondary" disabled={busy} onClick={() => { const er = check(); if (er) setError(ERRS[er]); else void save(); }}>Try Save again</Button>
                </AlertDescription>
              </Alert>
            ) : null}
            <div><Label htmlFor="pa-act">What to do</Label>
              <Pick id="pa-act" value={act} onChange={(a) => set({ act: a })} options={acts} /></div>
            {act === "apply-version" ? (
              <div><Label htmlFor="pa-ver">Previous plan</Label>
                <Pick id="pa-ver" value={f.ref} onChange={(ref) => set({ ref })} options={[["", "Pick one"], ...olds.map((x): [string, string] => [x.ref_id, oldTxt(x)])]} />
                <Help>It is saved as a new version, with its dose, step, stomach comfort, product and instructions.</Help></div>
            ) : act === "pause" ? (
              <Help>The dose is held; nothing moves until a team member sets the next dose.</Help>
            ) : (
              <>
                {steps.length || stepsWait ? (
                  <div><Label htmlFor="pa-step">Step</Label>
                    <Pick id="pa-step" value={stepText} invalid={fieldErr?.field === "step"} disabled={stepsWait || !!f.product_key}
                      onChange={(st) => { setFieldErr(null); set({ step: st, dose: st ? String(stepDose(steps, st) ?? "") : null }); }}
                      options={[...(!steps.some((x) => String(x.step) === stepText) ? [["", "Between steps"] as [string, string]] : []), ...stepOptions(steps, nowStep)]} />
                    <Help>{f.product_key ? "The new product has its own steps: set the dose." : "Picking a step sets the dose to that step. The plan climbs from there."}</Help>
                    {fieldErr?.field === "step" ? <FieldError id="pa-step-err">{fieldErr.text}</FieldError> : null}</div>
                ) : null}
                <div className="grid grid-cols-2 gap-3">
                  <div><Label htmlFor="pa-dose">Dose each time (ml)</Label>
                    <Input id="pa-dose" type="number" step="any" min="0" value={doseText}
                      aria-invalid={fieldErr?.field === "dose" || doseOver || undefined}
                      aria-describedby={fieldErr?.field === "dose" || doseOver ? "pa-dose-err" : "pa-dose-help"}
                      onChange={(e) => { setFieldErr(null); set({ dose: e.target.value, step: String(doseStep(steps, e.target.value === "" ? null : Number(e.target.value)) ?? "") }); }} />
                    {fieldErr?.field === "dose" ? <FieldError id="pa-dose-err">{fieldErr.text}</FieldError>
                      : doseOver ? <FieldError id="pa-dose-err">{aboveTarget(target)}</FieldError>
                      : <Help id="pa-dose-help">{pickedStep != null ? `Set by step ${pickedStep}. ` : ""}Up to {target ?? "—"} ml, her target.</Help>}</div>
                  <div><Label htmlFor="pa-wstep">Weekly increase (ml a week)</Label>
                    <Input id="pa-wstep" type="number" step="any" min="0" disabled={f.wdef || paused} value={wText}
                      onChange={(e) => set({ wstep: e.target.value })} />
                    <Help>0 holds the dose.</Help>
                    {s.weekly_step_set_by_staff && !paused ? (
                      <Label className="mt-1 font-normal"><Checkbox checked={f.wdef} onCheckedChange={(c) => set({ wdef: c === true, wstep: null })} /> Back to the protocol default</Label>
                    ) : null}</div>
                </div>
                <div><Label htmlFor="pa-rung">Stomach comfort (how she takes it)</Label>
                  <Pick id="pa-rung" value={rungText} onChange={(rung) => set({ rung })} options={COMFORT} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label htmlFor="pa-goal">Goal</Label>
                    <Pick id="pa-goal" value={f.goal_top || topNow} disabled={!chg || paused}
                      onChange={(g) => set({ goal_top: g === topNow ? "" : g, goal_level_2: "" })}
                      options={[...(tops.includes(topNow) ? [] : [[topNow, topNow || "—"] as [string, string]]), ...tops.map((g): [string, string] => [g, g])]} />
                    {subs.length > 1 || f.goal_top ? (
                      <div className="mt-2"><Label htmlFor="pa-sub">Which one</Label>
                        <Pick id="pa-sub" value={f.goal_level_2 || (f.goal_top ? "" : goalNow)} disabled={!chg || paused}
                          onChange={(g) => set({ goal_level_2: !f.goal_top && g === goalNow ? "" : g })}
                          options={[...(f.goal_top ? [["", "Pick one"] as [string, string]] : []), ...subs.map((g): [string, string] => [g.goal_level_2, g.goal_level_2])]} /></div>
                    ) : null}</div>
                  <div><Label htmlFor="pa-prod">Product</Label>
                    <Pick id="pa-prod" value={f.product_key || prodNow} disabled={!chg || paused}
                      onChange={(k) => set({ product_key: k === prodNow ? "" : k, step: null })}
                      options={productChoices(products, prodNow, plan.product)} /></div>
                </div>
                <div><Label htmlFor="pa-instr">How to take it (she sees this)</Label>
                  <Textarea id="pa-instr" rows={2} maxLength={500} disabled={!chg || paused} value={instrText}
                    onChange={(e) => set({ instructions: e.target.value })} /></div>
              </>
            )}
            <div><Label htmlFor="pa-reason">Reason (required)</Label>
              <Textarea id="pa-reason" rows={3} value={f.reason} onChange={(e) => set({ reason: e.target.value })} />
              <Help>Write why. It’s how we improve the algorithm.</Help></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label htmlFor="pa-ct">Case type</Label>
                <Pick id="pa-ct" value={f.case_type} onChange={(case_type) => set({ case_type })} options={[["", "Pick one"], ...caseTypes]} /></div>
              <div><Label htmlFor="pa-cid">Case ID</Label>
                <Input id="pa-cid" readOnly data-nonmutating aria-readonly="true" className="bg-ks-grey-4/40"
                  value={caseId ?? "New case ID on save"} />
                <Help>{caseId ? "The case of the item this answers." : "Made automatically when you save."}</Help></div>
            </div>
            {links.length ? (
              <div><Label htmlFor="pa-link">Answers</Label>
                <Pick id="pa-link" value={f.link} onChange={(link) => { set({ link }); setCaseGone(false); setLinkNote(null); }} options={[["", "Nothing open"], ...links]} /></div>
            ) : null}
            {pq != null ? (
              <div className="rounded-lg bg-ks-purple-4 p-2">
                <span className="block text-xs text-ks-grey-1">New journey preview</span>
                {pv.isLoading ? "…" : pv.error ? (pv.error as Error).message : journeyLine(pv.data)}
              </div>
            ) : null}
            {linkNote ? <Alert><AlertDescription>{linkNote}</AlertDescription></Alert> : null}
            {error ? <Alert><AlertDescription>{error}</AlertDescription></Alert> : null}
            <div className="flex gap-2">
              <Button variant="outline" data-nonmutating onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button disabled={!state.data} data-nonmutating onClick={() => {
                const er = check();
                if (doseOver) { setFieldErr({ field: "dose", text: aboveTarget(target) }); return; }
                if (er) { setError(ERRS[er]); return; }
                setError(null); setBlocked(null); setCaseGone(false); setFrozen(act); setStage("review");
              }}>See before and after</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3 px-4 pb-4 text-sm">
            <Table>
              <TableHeader>
                <TableRow><TableHead /><TableHead>Before</TableHead><TableHead>After</TableHead></TableRow>
              </TableHeader>
              <TableBody>
                {reviewRows.map(([k, b, a]) => (
                  <TableRow key={k}>
                    <TableHead>{k}</TableHead>
                    <TableCell className="whitespace-normal">{b}</TableCell>
                    <TableCell className={`whitespace-normal ${a !== b ? "font-semibold" : ""}`}>{a}</TableCell>
                  </TableRow>
                ))}
                <TableRow><TableHead>Reason</TableHead><TableCell colSpan={2} className="whitespace-normal">{f.reason.trim()}</TableCell></TableRow>
                {/* Damien 31 Oct review nit: staff see what the change answers and the case it files under. */}
                <TableRow><TableHead>Answers</TableHead><TableCell colSpan={2} className="whitespace-normal">{linkLabel(links, f.link)}</TableCell></TableRow>
                <TableRow><TableHead>Case ID</TableHead><TableCell colSpan={2} className="whitespace-normal">{caseId ?? "New case ID on save"}</TableCell></TableRow>
              </TableBody>
            </Table>
            {pq != null ? (
              <div className="rounded-lg bg-ks-purple-4 p-2">
                <span className="block text-xs text-ks-grey-1">New journey</span>
                {pv.isLoading ? "…" : pv.error ? (pv.error as Error).message : journeyLine(pv.data)}
              </div>
            ) : null}
            <p className="text-ks-grey-1">
              {first} accepts this in the app next time she opens it. Her next check-in is due 7 days after this change.
            </p>
            {linkNote ? <Alert><AlertDescription>{linkNote}</AlertDescription></Alert> : null}
            {error ? <Alert><AlertDescription>{error}</AlertDescription></Alert> : null}
            <div className="flex gap-2">
              <Button variant="outline" data-nonmutating onClick={() => { setStage("edit"); setFrozen(null); }}>Back</Button>
              <Button disabled={busy} onClick={save}>Save</Button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
